import type { Kalkis, Para, SecenekOteli } from './tipler';

/**
 * Kalkış listesi mantığı (saf işlevler; React yok, `api/test/unit/tour-factsheet-form-kalkis.spec.ts` bunları sınar).
 *
 * 13.10.2026, Ürün Sahibi: (1) kalkış listesinde "Tümünü seç" / "Tümünü kaldır"; seçili (kilitli) kalkış kalır.
 * (2) Aynı tarih üç kez görünüyor (18.11.2026 x3): aynı tarihli satırlar ayırt edilir.
 */

/** Tümünü seç: bütün kalkışlar PDF'e girer (sıra tur sırası). */
export function tumunuSec(kalkislar: readonly Kalkis[]): string[] {
  return kalkislar.map((k) => k.kalkisId);
}

/**
 * Tümünü kaldır: yalnız KİLİTLİ kalkış (varyant teklifinde seçili varyantın kalkışı) kalır. Kilitli yoksa (broşür) en az
 * bir kalkış seçili kalması kuralı yüzünden o an seçili olanların İLKİ kalır; hiç seçili yoksa listenin ilki.
 */
export function tumunuKaldir(kalkislar: readonly Kalkis[], kilitli: string | null, secili: readonly string[]): string[] {
  if (kilitli !== null && kalkislar.some((k) => k.kalkisId === kilitli)) return [kilitli];
  const ilk = kalkislar.find((k) => secili.includes(k.kalkisId)) ?? kalkislar[0];
  return ilk ? [ilk.kalkisId] : [];
}

/**
 * Kalkışın tek oteli (v3 `secenekOteli`, otel bağlı olmayan tur): "4 yıldız · 4* Oteller Yerel Hizmetler Opsiyonu". Ad yoksa
 * yalnız yıldız, yıldız yoksa yalnız ad; ikisi de yoksa (ya da alan hiç yoksa) null: çağıran eski gösterime döner.
 */
export function secenekOteliMetni(otel: SecenekOteli | null | undefined, yildizYaz: (yildiz: number) => string): string | null {
  if (!otel) return null;
  const ad = otel.otelAdi?.trim() || null;
  const parcalar = [otel.yildiz === null ? null : yildizYaz(otel.yildiz), ad].filter((p): p is string => p !== null);
  return parcalar.length ? parcalar.join(' · ') : null;
}

/** Kalkışlardaki farklı oteller: tekilleştirilir, yıldıza göre (3, 4, 5; yıldızsız en sonda) ve ada göre sıralanır. */
export function secenekOtelleri(kalkislar: readonly Kalkis[]): readonly SecenekOteli[] {
  const gorulen = new Map<string, SecenekOteli>();
  for (const k of kalkislar) {
    const o = k.secenekOteli;
    if (!o) continue;
    const ad = o.otelAdi?.trim() || null;
    if (o.yildiz === null && ad === null) continue;
    const anahtar = `${String(o.yildiz)}|${ad ?? ''}`;
    if (!gorulen.has(anahtar)) gorulen.set(anahtar, { yildiz: o.yildiz, otelAdi: ad });
  }
  return [...gorulen.values()].sort((a, b) => (a.yildiz ?? 99) - (b.yildiz ?? 99) || (a.otelAdi ?? '').localeCompare(b.otelAdi ?? '', 'tr'));
}

/** Kalkış düzeyi fiyat sütunu: otel sınıfı sütunu YOKSA ve en az bir kalkışta `kalkisFiyati` varsa tek bir "Fiyat" sütunu çıkar. */
export function kalkisFiyatSutunuVar(kalkislar: readonly Kalkis[], sinifSayisi: number): boolean {
  return sinifSayisi === 0 && kalkislar.some((k) => k.kalkisFiyati !== null && k.kalkisFiyati !== undefined);
}

/**
 * Kalkış satırının ikinci satırı: kalkış kimliği -> metin.
 *  - Kalkışın tek oteli (`secenekOteli`) varsa o yazılır ("Seçenek n" yerine). Fiyat sütunu yoksa ve aynı tarih birden çok
 *    kalkışta geçiyorsa otelin yanına kişi başı fiyat eklenir.
 *  - Oteli yoksa eski kural: aynı tarihli satırlara "Seçenek 2 · 993,37 USD"; tarihi tek olan satır haritada yoktur.
 */
export function ayirtBilgileri(
  kalkislar: readonly Kalkis[],
  yaz: {
    readonly para: (p: Para) => string;
    readonly secenek: (sira: number) => string;
    /** Verilirse kalkışın oteli bu işlevle yazılır (yıldız adı). */
    readonly yildiz?: (yildiz: number) => string;
    /** true: ayrı bir "Fiyat" sütunu var, satırın altına fiyat yazılmaz. */
    readonly fiyatSutunu?: boolean;
  },
): ReadonlyMap<string, string> {
  const sayac = new Map<string, number>();
  for (const k of kalkislar) sayac.set(k.tarih.slice(0, 10), (sayac.get(k.tarih.slice(0, 10)) ?? 0) + 1);
  const sira = new Map<string, number>();
  const sonuc = new Map<string, string>();
  for (const k of kalkislar) {
    const gun = k.tarih.slice(0, 10);
    const n = (sira.get(gun) ?? 0) + 1;
    sira.set(gun, n);
    const tekrar = (sayac.get(gun) ?? 0) >= 2;
    const otel = yaz.yildiz ? secenekOteliMetni(k.secenekOteli, yaz.yildiz) : null;
    if (!otel && !tekrar) continue;
    const fiyat = yaz.fiyatSutunu && otel ? null : (k.kisiBasi ?? k.sinifFiyatlari.find((f) => f.fiyat !== null)?.fiyat ?? k.kalkisFiyati ?? null);
    const ad = otel ?? yaz.secenek(n);
    sonuc.set(k.kalkisId, fiyat && (tekrar || !otel) ? `${ad} · ${yaz.para(fiyat)}` : ad);
  }
  return sonuc;
}

/** Fiyat kartı: TOPLAM ana (büyük), kişi başı ikincil. Sıra bu işlevden çıkar, çizim bu sırayı izler. */
export function fiyatKartiSirasi(fiyat: { readonly toplam: Para | null; readonly kisiBasi: Para | null }): { readonly ana: Para | null; readonly ikincil: Para | null } {
  return { ana: fiyat.toplam, ikincil: fiyat.kisiBasi };
}
